import type { ComponentType } from "react";
import { QueryViz } from "./Query.js";
import { MetricViz } from "./Metric.js";
import { HtmlViz } from "./Html.js";
import { TableViz } from "./Table.js";
import { BarChartViz } from "./Bar.js";
import { LineChartViz } from "./Line.js";
import { WaterfallViz } from "./Waterfall.js";
import { FunnelViz } from "./Funnel.js";
import { SqlEmbedEditor } from "./SqlEmbedEditor.js";

export interface VizProps {
  attrs: Record<string, string>;
  source: string;
}

/**
 * Props an embed editor receives. `updateCode` rewrites the underlying fence
 * in the page markdown: pass the new body, and optionally new attrs to also
 * rewrite the info string. Calling it is the whole contract — the renderer
 * splices it into the draft and everything re-renders.
 */
export interface VizEditorProps {
  attrs: Record<string, string>;
  source: string;
  updateCode: (body: string, attrs?: Record<string, string>) => void;
}

export interface VizEntry {
  /** Rendered view of the embed. */
  view: ComponentType<VizProps>;
  /** Optional custom editor; falls back to the raw fence editor if absent. */
  editor?: ComponentType<VizEditorProps>;
  /** Human label for the details panel, e.g. "Metric". Defaults to the kind. */
  label?: string;
}

/**
 * Map of fence-info kind → viz entry.
 *
 * To add a visualization, drop a component under viz/ and register it here
 * with at least a `view`. The renderer hands the view the fence body
 * (`source`) and `attrs`; if the page is being edited it also offers an
 * editor (custom, or the raw fence editor) wired to `updateCode`.
 */
export const vizRegistry: Record<string, VizEntry> = {
  query: { view: QueryViz, editor: SqlEmbedEditor, label: "Query" },
  metric: { view: MetricViz, editor: SqlEmbedEditor, label: "Metric" },
  html: { view: HtmlViz, label: "HTML" },
  table: { view: TableViz, editor: SqlEmbedEditor, label: "Table" },
  bar: { view: BarChartViz, editor: SqlEmbedEditor, label: "Bar chart" },
  line: { view: LineChartViz, editor: SqlEmbedEditor, label: "Line chart" },
  waterfall: { view: WaterfallViz, editor: SqlEmbedEditor, label: "Waterfall" },
  funnel: { view: FunnelViz, editor: SqlEmbedEditor, label: "Funnel" },
};
